// Opening / period / calculated closing cash & bank, receipts & payments and inter-branch money by cash/bank.
// Figures come from recorded source transactions; the ledger is shown alongside for comparison only.
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

const f = (n) => `SAR ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cls = (n) => (Number(n || 0) < 0 ? 'text-error' : '');

function Line({ label, value, testId, bold, link }) {
  return (
    <div className={`flex justify-between text-xs py-0.5 ${bold ? 'font-semibold' : ''}`} data-testid={testId}>
      <span className="text-muted-foreground">{link ? <Link to={link} className="hover:underline text-info">{label}</Link> : label}</span>
      <span className={`font-mono ${cls(value)}`}>{f(value)}</span>
    </div>
  );
}

function PlatformReceivable({ pr }) {
  if (!pr || (!pr.sales_to_date && !pr.settled_sales_to_date)) return null;
  return (
    <div data-testid="platform-receivable">
      <div className="text-xs font-semibold mb-1">Platform receivable (delivery platforms — not in Cash or Bank)</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <Line label="Platform sales (period)" value={pr.sales_period} testId="pr-sales-period" />
        <Line label="Settled sales covered (period) — estimated" value={pr.settled_sales_period} testId="pr-settled-period" />
        <Line label="Settlement received — Estimated, allocated by sales share, not bank-verified" value={pr.received_period} testId="pr-received-period" />
        <Line label="Platform commission — estimated by sales share" value={pr.commission_period} testId="pr-commission-period" />
        <Line label="Unverified difference (settled − received − commission)" value={pr.unexplained_period} testId="pr-unexplained" />
        <Line label="Outstanding receivable (calculated, to period end)" value={pr.outstanding_calculated} testId="pr-outstanding" bold />
      </div>
      <div className="text-[10px] text-muted-foreground" data-testid="pr-basis">{pr.basis}</div>
      {(pr.flags || []).length > 0 && <div className="text-[10px] text-amber-700" data-testid="pr-flags">UNVERIFIED: {pr.flags.join(', ').replace(/_/g, ' ').toLowerCase()}</div>}
    </div>
  );
}

export function BranchBalancesPanel({ b, startDate, endDate, hidePlatform = false }) {
  const bal = b.balances;
  if (!bal) return null;
  const rp = b.receipts_payments || {};
  const ib = b.inter_branch_by_mode || {};
  const q = `branch=${b.branch_id}&start_date=${startDate || ''}&end_date=${endDate || ''}`;
  const lk = (path) => (b.branch_id === '__unassigned__' ? null : `${path}?${q}`);
  const led = bal.ledger;
  return (
    <div className="rounded-xl border border-border p-3 space-y-3" data-testid={`branch-balances-${b.branch_id}`}>
      <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="balances-basis">
        <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {bal.basis}. Opening = all recorded movements before the period, not a verified starting balance.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {['cash', 'bank'].map((k) => (
          <div key={k} data-testid={`balances-${k}`}>
            <div className="text-xs font-semibold capitalize mb-1">{k}</div>
            <Line label="Opening movements (before period)" value={bal[k]?.opening_movements} testId={`${k}-opening`} />
            <Line label="Period movement" value={bal[k]?.period_movement} testId={`${k}-period`} />
            <Line label="Calculated closing (not verified)" value={bal[k]?.calculated_closing} testId={`${k}-closing`} bold />
            {led && <Line label="Ledger closing (incomplete — comparison only)" value={led.closing?.[k]} testId={`${k}-ledger-closing`} />}
            {led && <Line label="Difference (calculated − ledger)" value={(bal[k]?.calculated_closing || 0) - (led.closing?.[k] || 0)} testId={`${k}-ledger-diff`} />}
          </div>
        ))}
      </div>
      {!hidePlatform && <PlatformReceivable pr={b.cash_position?.platform_receivable} />}
      <div className="text-[11px] text-muted-foreground" data-testid="bank-accounts-openings">
        {(bal.bank_accounts || []).map((a) => <div key={a.id}>{a.name}: recorded opening {f(a.recorded_opening_balance)} — {a.opening_status}</div>)}
        <div data-testid="bank-attribution-note">Bank figures are for the branch as a whole — per-bank-account balances are not shown because transactions do not record which bank account was used.</div>
      </div>
      <div>
        <div className="text-xs font-semibold mb-1">Receipts &amp; payments (period)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
          <Line label="Cash received" value={rp.cash_received} testId="rp-cash-received" link={lk('/sales')} />
          <Line label="Cash paid" value={rp.cash_paid} testId="rp-cash-paid" link={lk('/expenses')} />
          <Line label="Bank received" value={rp.bank_received} testId="rp-bank-received" link={lk('/sales')} />
          <Line label="Bank paid" value={rp.bank_paid} testId="rp-bank-paid" link={lk('/expenses')} />
          {b.pending_review_supplier_payments?.count > 0 && <div className="text-[11px] text-amber-700" data-testid="pending-review-sp">Pending review (bank auto-match, not confirmed): {b.pending_review_supplier_payments.count} supplier payment(s), {Number(b.pending_review_supplier_payments.amount).toLocaleString()} — excluded from paid totals; {b.pending_review_supplier_payments.already_in_general_ledger} already posted in the General Ledger</div>}
      {rp.card_received > 0 && <Line label="Card received — awaiting terminal settlement (not in Cash or Bank)" value={rp.card_received} testId="rp-card-received" />}
          <div className="md:col-span-2 text-[10px] text-muted-foreground" data-testid="rp-paid-note">Paid figures include salary and supplier payments; the Expenses link lists expenses only.</div>
          {rp.unattributed_paid > 0 && <Line label="UNATTRIBUTED supplier payments (no payment method)" value={rp.unattributed_paid} testId="rp-unattributed" />}
        </div>
      </div>
      {b.branch_id !== '__unassigned__' && (
        <div data-testid="ib-by-mode">
          <div className="text-xs font-semibold mb-1">Inter-branch money (period)</div>
          {[['paid_for_other', 'Paid by this branch for other branches'], ['paid_by_other', 'Paid by other branches on its behalf']].map(([k, label]) => (
            <div key={k} className="text-xs flex flex-wrap gap-x-4 py-0.5" data-testid={`ib-${k}`}>
              <span className="text-muted-foreground w-full md:w-auto">{label}:</span>
              {Object.keys(ib[k] || {}).length === 0 ? <span>none</span> : Object.entries(ib[k]).map(([m, v]) => <span key={m} className="font-mono">{m} {f(v)}</span>)}
            </div>
          ))}
          <Link to="/inter-branch-ledger" className="text-[11px] text-info hover:underline" data-testid="ib-ledger-link">Open Inter-Branch Ledger →</Link>
        </div>
      )}
    </div>
  );
}
